// src/pages/SelfCheckInPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
import { Html5Qrcode } from 'html5-qrcode';

export default function SelfCheckInPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanResult, setScanResult] = useState({ type: '', message: '' });
  const [checkInMethod, setCheckInMethod] = useState('qr'); // 'qr' or 'email'
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [isScannerRunning, setIsScannerRunning] = useState(false);
  const qrReaderRef = useRef(null);

  // 사용자 이메일 자동 입력
  useEffect(() => {
    if (user?.email) {
      setEmailInput(user.email);
    }
  }, [user]);

  // 이벤트 정보 로드
  useEffect(() => {
    const loadEvent = async () => {
      try {
        const response = await axios.get(`/api/events/detail?eventId=${eventId}`);
        setEvent(response.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    loadEvent();
  }, [eventId]);

  // 이벤트 시간 체크
  const isEventTime = () => {
    if (!event) return false;
    const now = new Date();
    const eventTime = new Date(event.eventDateTime);
    // 이벤트 시작 30분 전부터 체크인 가능
    const checkInStartTime = new Date(eventTime.getTime() - 30 * 60 * 1000);
    return now >= checkInStartTime;
  };

  // QR 코드 스캔 처리
  const handleQrScan = (result) => {
    try {
      if (!result) {
        setScanResult({ type: 'error', message: 'Unable to recognize QR code' });
        return;
      }

      const data = JSON.parse(result);
      
      // 이벤트 ID 확인
      if (data.eventId && parseInt(data.eventId) === parseInt(eventId)) {
        // 로그인하지 않은 사용자는 이메일 체크인으로 안내
        if (!user.isLoggedIn) {
          setScanResult({ 
            type: 'error', 
            message: 'Please use email check-in method or log in first (Reason: not logged in)' 
          });
          return;
        }
        
        // processCheckIn에서 userId를 자동으로 가져오므로 바로 호출
        processCheckIn();
      } else {
        setScanResult({ type: 'error', message: 'Invalid event QR code' });
      }
    } catch (e) {
      setScanResult({ type: 'error', message: 'Invalid QR code format' });
    }
  };

  // QR 스캐너 초기화
  useEffect(() => {
    if (checkInMethod === 'qr' && !scanner && qrReaderRef.current) {
      const html5Qrcode = new Html5Qrcode("qr-reader");
      
      const config = {
        fps: 30,  // 10 → 30으로 증가 (더 빠른 스캔)
        qrbox: { width: 300, height: 300 },  // 스캔 영역 확대
        aspectRatio: 1.0
      };
      
      html5Qrcode.start(
        { facingMode: "environment" }, // 후면 카메라 사용
        config,
        (decodedText) => {
          handleQrScan(decodedText);
          // 스캔 성공 시 안전하게 중지
          html5Qrcode.stop().then(() => {
            setIsScannerRunning(false);
            setScanner(null);
          }).catch(() => {
            setIsScannerRunning(false);
            setScanner(null);
          });
        },
        (error) => {
          // 에러는 무시 (연속적인 스캔 시도)
        }
      ).then(() => {
        setIsScannerRunning(true);
        setScanner(html5Qrcode);
      }).catch((err) => {
        setScanResult({ type: 'error', message: 'Camera access failed' });
        setIsScannerRunning(false);
        setScanner(null);
      });
    }
    
    return () => {
      if (scanner) {
        scanner.stop().then(() => {
          setIsScannerRunning(false);
          setScanner(null);
        }).catch(() => {
          setIsScannerRunning(false);
          setScanner(null);
        });
      }
    };
  }, [checkInMethod]);

  // QR 스캐너 시작 함수
  const startQrScanner = () => {
    if (scanner || isScannerRunning) return;
    
    const html5Qrcode = new Html5Qrcode("qr-reader");
    
    const config = {
      fps: 30,
      qrbox: { width: 300, height: 300 },
      aspectRatio: 1.0
    };
    
    html5Qrcode.start(
      { facingMode: "environment" },
      config,
      (decodedText) => {
        handleQrScan(decodedText);
        html5Qrcode.stop().then(() => {
          setIsScannerRunning(false);
          setScanner(null);
        }).catch(() => {
          setIsScannerRunning(false);
          setScanner(null);
        });
      },
      (error) => {
        // 에러는 무시
      }
    ).then(() => {
      setIsScannerRunning(true);
      setScanner(html5Qrcode);
    }).catch((err) => {
      setScanResult({ type: 'error', message: 'Camera access failed' });
      setIsScannerRunning(false);
      setScanner(null);
    });
  };

  // 페이지 로드 시 QR 스캐너 자동 시작
  useEffect(() => {
    if (checkInMethod === 'qr' && !scanner && !isScannerRunning) {
      const timer = setTimeout(() => {
        if (qrReaderRef.current) {
          startQrScanner();
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [scanner, isScannerRunning, checkInMethod]);

  // 체크인 방법 변경 시 스캐너 정리
  useEffect(() => {
    if (checkInMethod !== 'qr' && scanner) {
      scanner.stop().then(() => {
        setIsScannerRunning(false);
        setScanner(null);
      }).catch(() => {
        setIsScannerRunning(false);
        setScanner(null);
      });
    }
  }, [checkInMethod, scanner]);

  // 이메일로 체크인
  const handleEmailCheckIn = async () => {
    if (!emailInput.trim()) {
      setScanResult({ type: 'error', message: 'Please enter your email address' });
      return;
    }

    await processCheckIn(emailInput);
  };

  // 체크인 처리
  const processCheckIn = async (email = null) => {
    setIsCheckingIn(true);
    try {
      let response;
      
      // 로그인하지 않은 사용자는 이메일로만 체크인 가능
      if (!user.isLoggedIn) {
        if (!email) {
          setScanResult({ 
            type: 'error', 
            message: 'Please log in or use email check-in method' 
          });
          setIsCheckingIn(false);
          return;
        }
        response = await axios.post(`/api/admin/check-in/email?eventId=${eventId}&userEmail=${email}`);
      } else if (email) {
        // 이메일 체크인
        response = await axios.post(`/api/admin/check-in/email?eventId=${eventId}&userEmail=${email}`);
      } else {
        // 로그인된 사용자 QR 체크인 (JWT 토큰 사용)
        response = await axios.post(`/api/admin/check-in/qr?eventId=${eventId}`);
      }
      
      if (response.data?.success) {
        const userName = response.data.userName || user.name || 'User';
        setScanResult({ 
          type: 'success', 
          message: `✅ Check-in completed! Welcome, ${userName}!` 
        });
        
        // 3초 후 이벤트 페이지로 이동
        setTimeout(() => {
          navigate(`/events/${eventId}`);
        }, 3000);
      } else {
        setScanResult({ 
          type: 'error', 
          message: response.data?.error || response.data?.message || 'Check-in failed' 
        });
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Check-in failed';
      setScanResult({ 
        type: 'error', 
        message: errorMessage
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading event information...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Event not found</h1>
          <button 
            onClick={() => navigate('/events')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  if (!isEventTime()) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Check-in time has not arrived</h1>
          <p className="text-gray-400 mb-6">
            Check-in is available 30 minutes before the event starts
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Event time: {new Date(event.eventDateTime).toLocaleString()}
          </p>
          <button 
            onClick={() => navigate(`/events/${eventId}`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Event Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-800 text-white p-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold mb-2">SLAM Check-in</h1>
          <h2 className="text-xl text-blue-400">{event.title}</h2>
          <p className="text-sm text-gray-400">
            {new Date(event.eventDateTime).toLocaleString()}
          </p>
          {!user.isLoggedIn && (
            <div className="bg-yellow-900 p-3 rounded-lg mt-4">
              <p className="text-yellow-200 text-sm">
                ⚠️ You need to be logged in to check in
              </p>
            </div>
          )}
        </div>

        {/* 체크인 방법 선택 */}
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">Choose Check-in Method</h3>
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => {
                // 이메일에서 QR로 변경 시 스캐너 강제 정리
                if (checkInMethod === 'email' && scanner) {
                  scanner.stop().then(() => {
                    setIsScannerRunning(false);
                    setScanner(null);
                    setCheckInMethod('qr');
                    // QR 모드로 변경 후 스캐너 시작
                    setTimeout(() => startQrScanner(), 100);
                  }).catch(() => {
                    setIsScannerRunning(false);
                    setScanner(null);
                    setCheckInMethod('qr');
                    setTimeout(() => startQrScanner(), 100);
                  });
                } else {
                  setCheckInMethod('qr');
                  // QR 모드로 변경 후 스캐너 시작
                  setTimeout(() => startQrScanner(), 100);
                }
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                checkInMethod === 'qr'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
              }`}
            >
              QR Code Scan
            </button>
            <button
              onClick={() => {
                // QR에서 이메일로 변경 시 스캐너 강제 정리
                if (checkInMethod === 'qr' && scanner) {
                  scanner.stop().then(() => {
                    setIsScannerRunning(false);
                    setScanner(null);
                    setCheckInMethod('email');
                  }).catch(() => {
                    setIsScannerRunning(false);
                    setScanner(null);
                    setCheckInMethod('email');
                  });
                } else {
                  setCheckInMethod('email');
                }
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                checkInMethod === 'email'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
              }`}
            >
              Email Input
            </button>
          </div>

          {/* QR 코드 스캔 */}
          {checkInMethod === 'qr' && (
            <div className="text-center">
              <p className="text-sm text-gray-400 mb-4">
                Please scan the QR code at the event venue
              </p>
              <div className="bg-black rounded-lg overflow-hidden shadow-2xl w-full aspect-square">
                <div id="qr-reader" ref={qrReaderRef} className="w-full h-full"></div>
              </div>
            </div>
          )}

          {/* 이메일 입력 */}
          {checkInMethod === 'email' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                {emailInput ? 'Your email is pre-filled. Click Check In to proceed.' : 'Please enter your registered email address'}
              </p>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="your@email.com"
                className="w-full p-3 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
              />
              <button
                onClick={handleEmailCheckIn}
                disabled={isCheckingIn}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingIn ? 'Checking in...' : 'Check In'}
              </button>
            </div>
          )}
        </div>

        {/* 체크인 결과 메시지 */}
        {scanResult.message && (
          <div className={`p-4 rounded-lg text-center font-bold text-lg transition-all ${
            scanResult.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {scanResult.message}
          </div>
        )}

        {/* 안내 메시지 */}
        <div className="bg-blue-900 p-4 rounded-lg text-center">
          <p className="text-sm text-blue-200">
            You can participate in the event after completing check-in
          </p>
        </div>
      </div>
    </div>
  );
}
